import React, { useMemo } from 'react';
import { Button, ScrollShadow, Spacer, useDisclosure } from '@nextui-org/react';
import { SidebarIcon as Icon } from './SidebarIcon';

import Sidebar from './SidebarTypes';
import { cn } from 'common/src/utils/cn';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import useDataStore from 'common/src/stores/useDataStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import SpeciesFilter from '../../../../../common/src/components/filters/SpeciesFilter';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import DivisionFilter from '../../../../../common/src/components/filters/DivisionFilter';
import useWarningsStore from '../../../store/warningsStore';
import useExporterLabelsStore from '../../../store/useExporterLabelsStore';
import SettlementWarningsModal from '../../SettlementWarningsModal';
import useBrand from 'common/src/hooks/useBrand';
import ProfileMenu from './ProfileMenu';
import { splitPinnedItems } from './pinnedItems';

interface DesktopSidebarProps {
  isCollapsed: boolean;
  location: string;
  sectionItemsWithTeams: any[];
  onToggle: () => void;
  onSelect: (key: string) => void;
  onLogout: () => void;
  /** Solo se pasa si el usuario aún no activó la verificación en dos pasos. */
  onOpenTwoFactor?: () => void;
}

const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  isCollapsed,
  location,
  sectionItemsWithTeams,
  onToggle,
  onSelect,
  onLogout,
  onOpenTwoFactor,
}) => {
  const isCompact = isCollapsed;
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const { labels, loading: labelsLoading } = useExporterLabelsStore();
  const { assets: brandAssets } = useBrand();
  const {
    codesMissingMapping,
    settlementLabelsMissingInMappings,
    settlementVarietiesOutsideSpecies,
    unclassifiedExporterCostsCount,
  } = useWarningsStore();
  const {
    isOpen: isWarningsOpen,
    onOpen: onWarningsOpen,
    onOpenChange: onWarningsOpenChange,
  } = useDisclosure();

  const canLoadLabels = useMemo(
    () =>
      Boolean(
        userProfile?.exporter_id && selectedSpecies?.id && selectedSeason
      ),
    [userProfile?.exporter_id, selectedSeason, selectedSpecies?.id]
  );

  const hasNoDeclaredLabels =
    canLoadLabels && !labelsLoading && labels.length === 0;

  const activeWarningsCount =
    (codesMissingMapping.length > 0 ? 1 : 0) +
    (settlementLabelsMissingInMappings.length > 0 ? 1 : 0) +
    (settlementVarietiesOutsideSpecies.length > 0 ? 1 : 0) +
    (unclassifiedExporterCostsCount > 0 ? 1 : 0) +
    (hasNoDeclaredLabels ? 1 : 0);

  // Determinar clave seleccionada para el sidebar
  const defaultKey = location.split('/')[1] || 'daily-market';
  // Configuración va fija al fondo, fuera de la lista.
  const { main: mainItems, pinned: pinnedItems } = useMemo(
    () => splitPinnedItems(sectionItemsWithTeams),
    [sectionItemsWithTeams]
  );
  const onSidebarSelect = (
    key: string | React.SyntheticEvent<HTMLUListElement, Event>
  ) => {
    // Si es un string, llamamos a onSelect
    if (typeof key === 'string') {
      onSelect(key);
    }
  };

  return (
    <div
      className={cn(
        'sticky top-0 flex h-screen w-72 flex-col !border-r-small border-divider p-6 transition-width bg-background',
        {
          'w-16 items-center px-2 py-6': isCompact,
        }
      )}
    >
      <div
        className={cn('flex items-center gap-3 px-3', {
          'justify-center gap-0': isCompact,
        })}
      >
        <div
          className={cn('flex min-w-0 flex-1 items-center', {
            'hidden flex-none': isCompact,
          })}
        >
          <img
            src={brandAssets.fullLogoLight}
            alt={brandAssets.name}
            className='h-auto w-full object-contain object-left opacity-100 dark:hidden'
          />
          <img
            src={brandAssets.fullLogoDark}
            alt={brandAssets.name}
            className='hidden h-auto w-full object-contain object-left opacity-100 dark:block'
          />
        </div>
        <Button
          isIconOnly
          size='sm'
          variant='light'
          className='h-5 w-5 min-w-5 flex-none'
          onPress={onToggle}
        >
          <Icon
            className='text-default-500'
            height={24}
            icon={
              isCollapsed ? 'eva:menu-outline' : 'eva:arrow-ios-back-outline'
            }
            width={24}
          />
        </Button>
      </div>
      <Spacer y={4} />

      {/* Perfil: abre el menú de la cuenta (idioma, tema, cerrar sesión). */}
      <ProfileMenu
        isCompact={isCompact}
        onLogout={onLogout}
        onOpenTwoFactor={onOpenTwoFactor}
      />

      <Spacer y={4} />

      {/* Filtro de especies */}
      {!(
        userProfile?.roles?.includes('superadmin') ||
        userProfile?.roles?.includes('document_helper')
      ) && (
        <div
          className={cn('mt-4 px-3 w-full', {
            'flex justify-center': isCompact,
          })}
        >
          <SpeciesFilter
            className={isCompact ? '' : 'w-full'}
            size='sm'
            collapsed={isCompact}
          />
        </div>
      )}
      {!(
        userProfile?.roles?.includes('superadmin') ||
        userProfile?.roles?.includes('document_helper')
      ) && (
        <div
          className={cn('mt-4 px-3 w-full', {
            'flex justify-center': isCompact,
          })}
        >
          <SeasonFilter
            className={isCompact ? '' : 'w-full'}
            size='sm'
            collapsed={isCompact}
          />
        </div>
      )}
      {!(
        userProfile?.roles?.includes('superadmin') ||
        userProfile?.roles?.includes('document_helper')
      ) && (
        <div
          className={cn('mt-4 px-3 w-full empty:hidden', {
            'flex justify-center': isCompact,
          })}
        >
          <DivisionFilter
            className={isCompact ? '' : 'w-full'}
            size='sm'
            collapsed={isCompact}
          />
        </div>
      )}
      {!isCompact && (
        <div className='mt-3 px-3 w-full'>
          {activeWarningsCount === 0 ? (
            <div className='rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-800 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300'>
              No hay alertas activas.
            </div>
          ) : (
            <div className='rounded-lg border border-rose-200 bg-rose-50 dark:bg-rose-950 dark:border-rose-800 px-3 py-2 text-sm text-rose-700 dark:text-rose-300 flex items-center justify-between gap-2'>
              <span>Hay {activeWarningsCount} alertas activas.</span>
              <button
                type='button'
                className='text-xs font-medium underline'
                onClick={onWarningsOpen}
              >
                Mostrar
              </button>
            </div>
          )}
        </div>
      )}
      <SettlementWarningsModal
        isOpen={isWarningsOpen}
        onOpenChange={onWarningsOpenChange}
      />

      <ScrollShadow className='-mr-6 h-full max-h-full py-6 pr-6'>
        <Sidebar
          selectedKeys={[defaultKey]}
          defaultSelectedKey={defaultKey}
          isCompact={isCompact}
          items={mainItems}
          onSelect={onSidebarSelect}
        />
      </ScrollShadow>
      <div
        className={cn('mt-auto flex flex-col pt-2', {
          'items-center': isCompact,
        })}
      >
        {pinnedItems.length ? (
          <Sidebar
            selectedKeys={[defaultKey]}
            defaultSelectedKey={defaultKey}
            isCompact={isCompact}
            items={pinnedItems}
            onSelect={onSidebarSelect}
          />
        ) : null}
        {/* Colapsado, el isotipo de la marca abajo. */}
        {isCompact && (
          <>
            <img
              src={brandAssets.collapsedLight}
              alt={`${brandAssets.name} Icon`}
              className='mt-4 h-12 w-12 object-contain dark:hidden'
            />
            <img
              src={brandAssets.collapsedDark}
              alt={`${brandAssets.name} Icon`}
              className='mt-4 hidden h-12 w-12 object-contain dark:block'
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DesktopSidebar;
